/**
 * Configurable columns for the cross-board search results table. The leading "Item" column (type
 * glyph + key + title) is always shown and carries the row link, so it isn't listed here — only the
 * columns a member can toggle / reorder. Mirrors the board Table's column model (pages/board/columns)
 * but tailored to cross-board results (adds Board / Type / Created / Updated; drops per-board dates).
 */
export type SearchColKey = 'board' | 'status' | 'priority' | 'assignee' | 'type' | 'epic' | 'parent' | 'labels' | 'source' | 'created' | 'updated';

export interface SearchColumnDef {
  key: SearchColKey;
  label: string;
  /** CSS grid track width. */
  width: string;
  align: 'left' | 'center';
  /** Whether the header sorts the (client-side) result list. */
  sortable: boolean;
}

export const SEARCH_COLUMNS: SearchColumnDef[] = [
  { key: 'board', label: 'Board', width: '168px', align: 'left', sortable: true },
  { key: 'status', label: 'Status', width: '132px', align: 'left', sortable: true },
  { key: 'priority', label: 'Priority', width: '80px', align: 'center', sortable: true },
  { key: 'assignee', label: 'Assignee', width: '156px', align: 'left', sortable: true },
  { key: 'type', label: 'Type', width: '116px', align: 'left', sortable: true },
  { key: 'epic', label: 'Epic', width: '176px', align: 'left', sortable: false },
  { key: 'parent', label: 'Parent', width: '176px', align: 'left', sortable: false },
  { key: 'labels', label: 'Labels', width: '184px', align: 'left', sortable: false },
  { key: 'source', label: 'Source', width: '148px', align: 'left', sortable: false },
  { key: 'created', label: 'Created', width: '104px', align: 'left', sortable: true },
  { key: 'updated', label: 'Updated', width: '104px', align: 'left', sortable: true },
];

const BY_KEY = new Map<SearchColKey, SearchColumnDef>(SEARCH_COLUMNS.map((c) => [c.key, c]));
const ALL_KEYS = new Set<SearchColKey>(SEARCH_COLUMNS.map((c) => c.key));
const CANONICAL_ORDER: SearchColKey[] = SEARCH_COLUMNS.map((c) => c.key);

export interface SearchColumnState {
  order: SearchColKey[];
  hidden: SearchColKey[];
}

/** Default: Board · Status · Priority · Assignee · Updated shown; the rest hidden. */
export const DEFAULT_SEARCH_COLUMNS: SearchColumnState = {
  order: [...CANONICAL_ORDER],
  hidden: ['type', 'epic', 'parent', 'labels', 'source', 'created'],
};

const dedupe = (keys: SearchColKey[]): SearchColKey[] => {
  const seen = new Set<SearchColKey>();
  const out: SearchColKey[] = [];
  for (const k of keys) if (!seen.has(k)) (seen.add(k), out.push(k));
  return out;
};
const knownKeys = (raw: unknown): SearchColKey[] =>
  Array.isArray(raw) ? dedupe(raw.filter((k): k is SearchColKey => typeof k === 'string' && ALL_KEYS.has(k as SearchColKey))) : [];
const withAllKeys = (keys: SearchColKey[]): SearchColKey[] => {
  const present = new Set(keys);
  return [...keys, ...CANONICAL_ORDER.filter((k) => !present.has(k))];
};

/** Coerce a persisted (untrusted) value into a valid SearchColumnState. */
export const sanitizeSearchColumns = (raw: unknown): SearchColumnState => {
  if (raw == null) return { order: [...DEFAULT_SEARCH_COLUMNS.order], hidden: [...DEFAULT_SEARCH_COLUMNS.hidden] };
  const obj = (raw ?? {}) as { order?: unknown; hidden?: unknown };
  const order = withAllKeys(knownKeys(obj.order));
  const hidden = new Set(knownKeys(obj.hidden));
  if (order.every((k) => hidden.has(k))) hidden.delete(order[0]);
  return { order, hidden: [...hidden] };
};

export const visibleSearchKeys = (state: SearchColumnState): SearchColKey[] =>
  state.order.filter((k) => !state.hidden.includes(k));

export const resolveSearchColumns = (keys: SearchColKey[]): SearchColumnDef[] =>
  keys.map((k) => BY_KEY.get(k)).filter((c): c is SearchColumnDef => !!c);

export const searchColumnLabel = (key: SearchColKey): string => BY_KEY.get(key)?.label ?? key;

/** The Item (title) column's min track. */
const ITEM_MIN = 300;

export const searchGridTemplate = (columns: SearchColumnDef[]): string =>
  `minmax(${ITEM_MIN}px, 1fr) ${columns.map((c) => c.width).join(' ')}`;

export const searchTableMinWidth = (columns: SearchColumnDef[]): number =>
  ITEM_MIN + columns.reduce((sum, c) => sum + (parseInt(c.width, 10) || 0), 0);

// ---- URL encoding (so a shared link reproduces the exact table) ----------------------------------

/** Encode the layout as the visible columns in display order, e.g. "board,status,priority". */
export const encodeVisibleCols = (state: SearchColumnState): string => visibleSearchKeys(state).join(',');

/** Decode a `cols=` param back into a full state (hidden = every column not listed). Falsy/invalid
 *  input → the default layout. */
export const decodeCols = (raw: string | null): SearchColumnState => {
  const vis = knownKeys((raw ?? '').split(',').map((s) => s.trim()));
  if (vis.length === 0) return { order: [...DEFAULT_SEARCH_COLUMNS.order], hidden: [...DEFAULT_SEARCH_COLUMNS.hidden] };
  const visSet = new Set(vis);
  return sanitizeSearchColumns({ order: vis, hidden: CANONICAL_ORDER.filter((k) => !visSet.has(k)) });
};
